import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CredentialType } from 'enums/CredentialType';
import { LogEntryType } from 'enums/LogEntryType';
import { SystemQuery } from 'enums/SystemQuery';
import { TaskActionType } from 'enums/TaskActionType';
import { environment } from 'environments/environment';
import { SelectListItemModel } from 'models/SelectList';
import { CommunicationLogEntryModel } from 'models/generated/Communication.Api.Models.CommunicationLogEntry';
import { QueryTaskActionModel } from 'models/generated/Entities.GormCrm.QueryTaskAction';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskAreaModel } from 'models/generated/Entities.GormCrm.TaskArea';
import { TaskAttachmentModel } from 'models/generated/Entities.GormCrm.TaskAttachment';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';
import { OrderOnboardingViewModel } from 'models/generated/Models.OrderOnboardingView';
import { PermissionModel } from 'models/generated/Models.Permission';
import { QualificationCallOverviewModel } from 'models/generated/Models.QualificationCallOverview';
import { GetResultFromSystemQueryRequestModel } from 'models/generated/Models.Query.GetResultFromSystemQueryRequest';
import { CloseTaskActionRequestModel } from 'models/generated/Models.Task.CloseTaskActionRequest';
import { CreateTaskActionWithNewTaskflowRequestModel } from 'models/generated/Models.Task.CreateTaskActionWithNewTaskflowRequest';
import { CreateTaskflowFromTagRequestModel } from 'models/generated/Models.Task.CreateTaskflowFromTagRequest';
import { RescheduleTaskActionRequestModel } from 'models/generated/Models.Task.RescheduleTaskActionRequest';
import { TaskActionDtoModel } from 'models/generated/Models.Task.TaskActionDto';
import { TaskflowDtoModel } from 'models/generated/Models.Task.TaskflowDto';
import { TranslatePipe } from 'pipes/translate.pipe';
import { firstValueFrom } from 'rxjs';
import { UserService } from 'services/user.service';

@Injectable()
export class TaskService {
	private baseUrl = `${environment.gatewayUrl}/api`;

	private _taskAreas?: TaskAreaModel[];
	private taskAreasPromise?: Promise<TaskAreaModel[]>;

	constructor(private readonly http: HttpClient, private readonly translate: TranslatePipe, private readonly userService: UserService) {
	}

	getTaskflow(id: string): Promise<TaskflowModel> {
		return firstValueFrom(this.http.get<TaskflowModel>(`${this.baseUrl}/taskflows/${id}`));
	}

	getTaskAction(id: string): Promise<TaskActionModel> {
		return firstValueFrom(this.http.get<TaskActionModel>(`${this.baseUrl}/task-actions/${id}`));
	}

	getTaskActions(ids: string[]): Promise<TaskActionModel[]> {
		return firstValueFrom(this.http.post<TaskActionModel[]>(`${this.baseUrl}/task-actions/get-multiple`, ids));
	}

	getUsersOpenTaskActionsQuery(userId: string): Promise<TaskActionModel[]> {
		return firstValueFrom(this.http.get<TaskActionModel[]>(`${this.baseUrl}/task-actions/users-open/${userId}`));
	}

	getUsersOpenTaskActions(userId: string): Promise<TaskActionModel[]> {
		const request = new GetResultFromSystemQueryRequestModel();
		request.systemQuery = SystemQuery.UsersOpenTaskActions;
		request.parameters = { assignedToUserId: userId };
		return firstValueFrom(this.http.post<TaskActionModel[]>(`${this.baseUrl}/task-actions/from-query/`, request));
	}

	getCompanyTaskflows(companyId: string): Promise<TaskflowModel[]> {
		return firstValueFrom(this.http.get<TaskflowModel[]>(`${this.baseUrl}/taskflows/company/${companyId}`));
	}

	getAllTaskAreas(): Promise<TaskAreaModel[]> {
		if (this._taskAreas) return new Promise((resolve) => { resolve(this._taskAreas!) });
		if (this.taskAreasPromise) return this.taskAreasPromise;
		return this.reloadTaskAreas();
	}

	createOrUpdateTaskArea(taskArea: TaskAreaModel): Promise<TaskAreaModel> {
		return firstValueFrom(this.http.post<TaskAreaModel>(`${this.baseUrl}/task-areas/`, taskArea)).then((newTaskArea: TaskAreaModel) => {
			return this.reloadTaskAreas().then(() => { return newTaskArea });
		});
	}

	deleteTaskArea(taskArea: TaskAreaModel): Promise<void> {
		return firstValueFrom(this.http.delete(`${this.baseUrl}/task-areas/${taskArea.id}`)).then(() => {
			this.reloadTaskAreas();
		});
	}

	getUnAssignedQualificationCalls(): Promise<QualificationCallOverviewModel[]> {
		return firstValueFrom(this.http.get<QualificationCallOverviewModel[]>(`${this.baseUrl}/taskflows/get-unassigned-qualification-calls`));
	}

	getTaskActionTypes(ignoreFilter: boolean = false): SelectListItemModel[] {

		let availableTaskTypes: TaskActionType[] = [TaskActionType.contact, TaskActionType.correction, TaskActionType.inquiry];

		let taskActionTypeList: SelectListItemModel[] = [];
		TaskActionType.getKeys().forEach((value: string, key: TaskActionType) => {
			if (availableTaskTypes.some(type => type === key) || ignoreFilter)
				taskActionTypeList.push(new SelectListItemModel(key.toString(), this.translate.transform('enum.task-action-type.' + value)));
		});

		return taskActionTypeList;
	}

	getCompanySalesflows(companyId: string): Promise<TaskflowModel[]> {
		return this.getTaskflowsFromQuery(SystemQuery.CompanySalesflows, { companyId: companyId });
	}

	getTaskActionsFromQuery(systemQuery: SystemQuery, parameters: object): Promise<TaskActionModel[]> {
		const request = new GetResultFromSystemQueryRequestModel();
		request.systemQuery = systemQuery;
		request.parameters = parameters;
		return firstValueFrom(this.http.post<TaskActionModel[]>(`${this.baseUrl}/task-actions/from-query/`, request));
	}

	getTaskflowsFromQuery(systemQuery: SystemQuery, parameters: object): Promise<TaskflowModel[]> {
		const request = new GetResultFromSystemQueryRequestModel();
		request.systemQuery = systemQuery;
		request.parameters = parameters;
		return firstValueFrom(this.http.post<TaskflowModel[]>(`${this.baseUrl}/taskflows/from-query/`, request));
	}

	getUsersOpenSalesflows(userId: string): Promise<TaskActionModel[]> {
		return firstValueFrom(this.http.get<TaskActionModel[]>(`${this.baseUrl}/task-actions/users-open-salesflows/${userId}`));
	}

	isTriggerTask(action: TaskActionModel): boolean {
		const triggerTypes: TaskActionType[] = [
			TaskActionType.designApprovalTrigger,
			TaskActionType.onboardingTrigger,
			TaskActionType.presentationTrigger,
			TaskActionType.dnsTrigger,
			TaskActionType.orderDkHostmasterDomainTrigger,
			TaskActionType.awaitDkHostmasterResponseTrigger,
			TaskActionType.openCallInDSv2Trigger,
			TaskActionType.closeCallInDSv2Trigger];
		return triggerTypes.some(type => type === action.type);
	}

	getOrderOnboardingTaskflows(): Promise<OrderOnboardingViewModel[]> {
		return firstValueFrom(this.http.get<OrderOnboardingViewModel[]>(`${this.baseUrl}/taskflows/order-onboarding`));
	}

	closeTaskAction(taskActionId: string, comments: string | null = null, logEntryStatus?: string, followUpDate: Date | null = null, followUpTaskType: TaskActionType | null = null, dateOnly: boolean = false, mailData: string | null = null): Promise<TaskActionModel[]> {
		const request = new CloseTaskActionRequestModel();
		request.taskActionId = taskActionId;
		request.logComments = comments == null ? '' : comments;
		request.logEntryStatus = logEntryStatus;
		request.followUpDate = followUpDate == null ? undefined : followUpDate;
		request.followUpTaskType = followUpTaskType == null ? undefined : followUpTaskType;
		request.dateOnly = dateOnly;
		request.mailData = mailData == null ? '' : mailData;

		return firstValueFrom(this.http.put<TaskActionModel[]>(`${this.baseUrl}/task-actions/close/`, request));
	}

	cloneTaskflowAndCloseTaskAction(taskflowId: string, taskActionId: string): Promise<TaskflowModel> {
		return (firstValueFrom(this.http.put<TaskflowModel>(`${this.baseUrl}/taskflows/${taskflowId}/clone-and-close/`, taskActionId)));
	}

	rescheduleTaskAction(taskAction: TaskActionModel, comments: string | null = null, logEntryStatus?: string): Promise<TaskActionModel> {
		const request = new RescheduleTaskActionRequestModel();
		request.updatedTaskAction = taskAction;
		request.logComments = comments == null ? '' : comments;
		request.logEntryStatus = logEntryStatus;
		request.include(r => r.updatedTaskAction);
		return firstValueFrom(this.http.put<TaskActionModel>(`${this.baseUrl}/task-actions/reschedule/`, request));
	}

	createTaskActionWithNewTaskflow(comments: string | null = null, logEntryStatus: string | undefined, followUpDate: Date | null, followUpTaskType: TaskActionType,
		taskflowTag: string, companyId: string, dateOnly: boolean, mailData: string | null = null): Promise<TaskActionModel> {

		const request = new CreateTaskActionWithNewTaskflowRequestModel();
		request.logComments = comments == null ? '' : comments;
		request.logEntryStatus = logEntryStatus;
		request.followUpDate = followUpDate == null ? undefined : followUpDate;
		request.followUpTaskType = followUpTaskType;
		request.taskflowTag = taskflowTag;
		request.companyId = companyId;
		request.dateOnly = dateOnly;
		request.mailData = mailData == null ? '' : mailData;
		return firstValueFrom(this.http.post<TaskActionModel>(`${this.baseUrl}/task-actions/create-with-new-taskflow/`, request));
	}

	createTaskflow(taskflow: TaskflowModel, selectedProductIds: string[]): Promise<TaskflowModel> {
		taskflow.include(tf => tf.taskActions);
		taskflow.taskActions.forEach(taskAction => taskAction.include(ta => ta.taskAttachments));
		const taskflowDto = new TaskflowDtoModel();
		taskflowDto.taskflow = taskflow;
		taskflowDto.productIds = selectedProductIds;
		taskflowDto.include(dto => dto.taskflow);
		return firstValueFrom(this.http.post<TaskflowModel>(`${this.baseUrl}/taskflows/`, taskflowDto));
	}

	createTaskflowFromTag(taskflowTag: string, companyId: string, actions: TaskActionModel[] | null = null, deadline: Date | null = null, selectedProductIds: string[] | null = null): Promise<TaskflowModel> {

		const request = new CreateTaskflowFromTagRequestModel();
		request.taskflowTag = taskflowTag;
		request.companyId = companyId;
		request.actions = actions == null ? [] : actions;
		request.deadline = deadline == null ? undefined : deadline;
		request.selectedProductIds = selectedProductIds == null ? [] : selectedProductIds;
		request.include(r => r.actions);
		return firstValueFrom(this.http.post<TaskflowModel>(`${this.baseUrl}/taskflows/from-tag`, request));
	}

	addTaskActionToTaskflow(taskAction: TaskActionModel): Promise<void> {
		taskAction.include(ta => ta.taskAttachments);

		return firstValueFrom(this.http.put(`${this.baseUrl}/task-actions/add-to-taskflow`, taskAction)).then(() => {
			return;
		});
	}

	//Update Taskflow
	updateTaskflowProduct(taskflowId: string, orderLineId: string): Promise<void> {
		const taskflowDto = new TaskflowDtoModel();
		taskflowDto.orderLineId = orderLineId;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/taskflows/${taskflowId}/update-product`, taskflowDto));
	}
	updateTaskflowTitle(taskflowId: string, title: string): Promise<void> {
		const taskflowDto = new TaskflowDtoModel();
		taskflowDto.title = title;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/taskflows/${taskflowId}/update-title`, taskflowDto));
	}
	updateTaskflowDescription(taskflowId: string, description: string): Promise<void> {
		const taskflowDto = new TaskflowDtoModel();
		taskflowDto.description = description;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/taskflows/${taskflowId}/update-description`, taskflowDto));
	}
	updateTaskflowOwner(taskflowId: string, userId?: string): Promise<void> {
		const taskflowDto = new TaskflowDtoModel();
		taskflowDto.userId = userId;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/taskflows/${taskflowId}/update-owner`, taskflowDto));
	}
	updateTaskflowDeadline(taskflowId: string, deadline?: Date): Promise<void> {
		const taskflowDto = new TaskflowDtoModel();
		taskflowDto.deadline = deadline;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/taskflows/${taskflowId}/update-deadline`, taskflowDto));
	}

	//Update TaskAction
	updateTaskActionDetails(taskActionId: string, details: string): Promise<void> {
		const taskActionDto = new TaskActionDtoModel();
		taskActionDto.details = details;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/task-actions/${taskActionId}/update-details`, taskActionDto));
	}
	updateTaskActionArea(taskActionId: string, taskAreaId: string): Promise<void> {
		const taskActionDto = new TaskActionDtoModel();
		taskActionDto.taskAreaId = taskAreaId;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/task-actions/${taskActionId}/update-area`, taskActionDto));
	}
	updateTaskActionAssignedTo(taskActionId: string, userId: string): Promise<void> {
		const taskActionDto = new TaskActionDtoModel();
		taskActionDto.userId = userId;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/task-actions/${taskActionId}/update-assigned-to`, taskActionDto));
	}
	updateTaskActionPlannedDate(taskActionId: string, date: Date, dateOnly: boolean): Promise<void> {
		const taskActionDto = new TaskActionDtoModel();
		taskActionDto.plannedDate = date;
		taskActionDto.dateOnly = dateOnly;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/task-actions/${taskActionId}/update-planned-date`, taskActionDto));
	}
	updateTaskActionInquiryReply(taskActionId: string, reply: string): Promise<void> {
		const taskActionDto = new TaskActionDtoModel();
		taskActionDto.inquiryReply = reply;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/task-actions/${taskActionId}/update-inquiry-reply`, taskActionDto));
	}
	updateTaskActionAssignedToAndTaskflowOwner(taskActionId: string, userId: string): Promise<void> {
		const taskActionDto = new TaskActionDtoModel();
		taskActionDto.userId = userId;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/task-actions/${taskActionId}/assigned-to-and-taskflow-owner`, taskActionDto));
	}

	//TaskAttachment
	getTaskAttachmentFileType(filename: string): string {
		return filename && filename.indexOf('.') >= 0 ? (filename.split('.').pop() as string).toLowerCase() : filename;
	}

	filterLogEntries(logEntries: CommunicationLogEntryModel[], filters: LogEntryType[]): CommunicationLogEntryModel[] {
		var entries: CommunicationLogEntryModel[] = [];
		logEntries.forEach(log => {
			if (filters.indexOf(log.type!) > -1)
				entries.push(log);
		});
		return entries;
	}

	getCompanyTaskAttachments(companyId: string): Promise<TaskAttachmentModel[]> {
		return firstValueFrom(this.http.get<TaskAttachmentModel[]>(`${this.baseUrl}/task-attachments/company/${companyId}`));
	}
	deleteTaskAttachments(fileIds: string[]): Promise<void> {
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/task-attachments/delete`, fileIds));
	}

	isTaskflowCreatedFromTag(taskflow: TaskflowModel, matchTag: string): boolean {
		if (!taskflow.sourceTemplate) throw ('Unable to determine source template for taskflow');
		return taskflow.sourceTemplate!.taskflowTemplateTags.some(tag => tag.tag === matchTag);
	}

	checkPermissionToEdit(taskflow: TaskflowModel, action: TaskActionModel): Promise<PermissionModel> {
		let permissions = new PermissionModel();
		permissions.taskflowAccess = true;
		permissions.taskActionAccess = true;
		if (this.isTaskflowCreatedFromTag(taskflow, 'permission.edit')) {
			permissions.taskflowAccess = false;
			permissions.taskActionAccess = false;

			const user = this.userService.getCurrentUser();
			const credentials = this.userService.getCredentials();

			if (taskflow.owner == user.id) {
				permissions.taskflowAccess = true;
				permissions.taskActionAccess = true;
				return new Promise((resolve) => { resolve(permissions) });
			}
			else if (action.assignedToUserId == user.id) {
				permissions.taskActionAccess = true;
			}
			if (credentials.some(credential => credential == CredentialType.TaskflowAccessEdit)) {
				permissions.taskflowAccess = true;
				permissions.taskActionAccess = true;
			}
			return new Promise((resolve) => { resolve(permissions) });
		}
		else {
			return new Promise((resolve) => { resolve(permissions) });
		}
	}

	private reloadTaskAreas(): Promise<TaskAreaModel[]> {
		return this.taskAreasPromise = firstValueFrom(this.http.get<TaskAreaModel[]>(`${this.baseUrl}/task-areas`)).then((taskAreas: TaskAreaModel[]) => {
			this._taskAreas = taskAreas;
			return taskAreas;
		});
	}

	getLogEntryStatusColor(status?: string, pureColorCode = false) {
		switch (status) {
			case 'rejected':
			case 'meeting-cancelled':
			case 'mail-offer-rejected':
			case 'offer-rejected':
				return pureColorCode ? 'color-red-50' : 'var(--color-red-50)';
			case 'dialog':
				return pureColorCode ? 'color-yellow-50' : 'var(--color-yellow-50)';
			case 'no-response':
				return pureColorCode ? 'color-orange-40' : 'var(--color-orange-40)';
			default:
				return pureColorCode ? 'color-green-50' : 'var(--color-green-50)';
		}
	}

	getOrderIdFromTaskflow(taskflowId: string): Promise<string> {
		return firstValueFrom(this.http.get<string>(`${this.baseUrl}/taskflows/${taskflowId}/order-id`));
	}
}
